import type { Faq, Image, ProductFilm, ProductTab } from './schema'
import { externalLinks } from './site'

export const kapeToolsPage = {
  eyebrow: 'Kape.Tools',
  titleThin: 'One tab for',
  titleBold: 'the whole freelance day.',
  lede: 'The workspace I built for Filipino freelancers and VAs: a task board, a client CRM with hours and invoices, free browser tools, job boards, and a Google Maps lead finder.',
  footnote: 'Built with the BrewedOps Agentic Framework, self-hosted on my own VPS.',
  cta: { label: 'Open Kape.Tools', href: externalLinks.kapeTools, external: true },
  roomsTitle: 'The five rooms',
}

/** A room capture (1600 wide) with 800 / 1200 copies: the rooms show at 343-970px (scripts/make-image-variants.py). */
const room = (file: string, alt: string, height: number): Image => ({
  src: `/images/kape-tools/${file}.webp`,
  alt,
  width: 1600,
  height,
  srcSet: `/images/kape-tools/800/${file}.webp 800w, /images/kape-tools/1200/${file}.webp 1200w, /images/kape-tools/${file}.webp 1600w`,
})

/** The live product film (kape.tools/tutorials/kapetools-film.mp4), re-encoded to 1.8 MB, no audio. */
export const kapeToolsFilm: ProductFilm = {
  src: '/media/kape-tools-film.mp4',
  poster: { src: '/images/kape-tools/film-poster.webp', alt: 'Kape.Tools product film', width: 1280, height: 720 },
  pagePosters: { phone: '/images/kape-tools/film-poster-640.webp', wide: '/images/kape-tools/film-poster-960.webp' },
  name: 'Kape.Tools: your whole workday, one tab',
  description: 'A 32-second tour of Kape.Tools: the Desk, three job boards in one place, leads from Google Maps, clients with invoices, and 40 free tools with no uploads.',
  duration: '0:32',
  // First published on kape.tools in commit 5b6d60e of the product repo.
  published: '2026-10-01',
}

/** Number + unit, each one stated on kape.tools or in the room captions below. */
export const kapeToolsStats: [string, string][] = [
  ['40+', 'browser tools'],
  ['3', 'job boards'],
  ['30', 'leads per search'],
  ['5', 'rooms, one tab'],
]

/** The five rooms of the product, captions verbatim from v1. */
export const productTabs: ProductTab[] = [
  {
    id: 'announcement',
    label: 'Announcement',
    body: 'The room you land in. It tells you what actually needs you today across every client, then gets out of the way. Under it sits the changelog, because people who pay for a tool deserve to see it move.',
    // The room itself is behind sign-in; this is the public home page until Kenneth sends a capture.
    image: room('landing', 'The Kape.Tools home page', 1000),
  },
  {
    id: 'desk',
    label: 'Desk',
    body: 'Task boards you drag, a spreadsheet with formulas, a content calendar, a snippet board, a Pomodoro timer, world clock, and a client list that tracks hours, invoices, and what has been paid.',
    image: room('scene-desk', 'The Desk: a Today list of tasks', 900),
  },
  {
    id: 'find-work',
    label: 'Find Work',
    body: 'OnlineJobs.ph, LinkedIn, and JobStreet PH pulled into one board, with an application tracker that logs what you sent, counts your reply rate, and flags anything that has gone quiet for two weeks.',
    image: room('app-job-board', 'The job board pulling OnlineJobs.ph, LinkedIn and JobStreet PH into one list', 1000),
  },
  {
    id: 'tools',
    label: 'Tools',
    body: 'Over 40 browser tools, most of them running on your own device so the file never leaves it. Convert, compress, split a PDF, pull text out of a screenshot, cut a background, upscale, transcribe, rewrite.',
    image: room('scene-tools', 'Forty free browser tools, no uploads', 900),
  },
  {
    id: 'lead-finder',
    label: 'Lead Finder',
    body: 'The Google Maps lead finder this whole product grew out of. Search a niche and a city, get up to 30 businesses with reviews, contacts, an opportunity score, and a pitch angle. Outreach mini-CRM included.',
    image: room('app-lead-finder', 'Lead Finder results for coffee shops in Cebu City', 1000),
  },
]

/** Answers verbatim from the product's own FAQ (kape.tools/faq), name spelled as on this site. */
export const kapeToolsFaq: Faq[] = [
  {
    question: 'Who is Kape.Tools for?',
    answer: 'Filipino freelancers and virtual assistants looking for work, freelancers who already have clients and need to track hours and invoices, and GoHighLevel operators running a small agency. It suits somebody doing all of that alone. One account is one person.',
  },
  {
    question: 'Is there anything to install?',
    answer: 'No. Kape.Tools is a website and it works in a phone browser, but there is nothing to install from an app store and nothing works offline.',
  },
  {
    question: 'Is Kape.Tools really free?',
    answer: 'Yes. The free plan costs nothing, does not expire and is not a trial. It covers the three job boards, the application tracker, the lead finder, the client list, the task boards and every browser-side tool.',
  },
]
